import type { Flow, FlowMap, FlowStep, FlowTrigger } from '@cogo/types-v2'
import type {
  AppSpecFull,
  StudioActionFlowBinding,
  StudioActionFlowBindingPlan,
  StudioActionFlowCompilerIssue,
  StudioActionFlowCompilerProof,
  StudioActionFlowPrimitive,
  StudioActionFlowStateNode,
  StudioActionFlowTopologyContract,
  StudioActionFlowGenerationAuthority,
} from '@/services/block-api-service'
import {
  buildActionFlowTopologyContract,
  withActionFlowTopologyContract,
} from './actionflow-topology'
import { buildActionFlowGenerationAuthority } from './actionflow-generation-authority'

interface BuildActionFlowBindingPlanOptions {
  source?: string
}

function generationTargetForAppSpec(
  appSpec: AppSpecFull,
): 'react' | 'flutter' {
  if (appSpec.generationTargetPlatform) return appSpec.generationTargetPlatform
  if (appSpec.targetPlatform === 'flutter') return 'flutter'
  if (
    appSpec.runtimeTargets?.includes('flutter') &&
    !appSpec.runtimeTargets.some((target) =>
      ['web', 'rn', 'desktop'].includes(target),
    )
  ) {
    return 'flutter'
  }
  return 'react'
}

const FLOW_TRIGGERS = new Set<FlowTrigger>([
  'event',
  'lifecycle',
  'init',
  'manual',
])

function stableId(value: string | undefined, fallback: string): string {
  const input = (value ?? fallback).trim().toLowerCase()
  const slug = input
    .replace(/[^a-z0-9:/._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || fallback
}

function flowNameForPrimitive(primitive: StudioActionFlowPrimitive): string {
  return `af_${stableId(`${primitive.kind}-${primitive.id}`, 'flow').replace(/[-:.]+/g, '_')}`
}

function resultKeyForPrimitive(primitive: StudioActionFlowPrimitive): string {
  const boundResultKey = primitive.compilerParamBindings?.find(
    (binding) => binding.role === 'result',
  )?.resultKey
  if (boundResultKey) return boundResultKey
  return `${stableId(primitive.kind, 'primitive').replace(/-/g, '')}Result`
}

function expressionForNode(
  node: StudioActionFlowStateNode | undefined,
): string {
  return `#{${node?.path ?? 'event.value'}}`
}

function setStateTargetForNode(
  node: StudioActionFlowStateNode | undefined,
): string {
  if (!node) return 'value'
  if (node.scope === 'local') return node.path.replace(/^local\./, '')
  return node.path
}

function firstBlockName(appSpec: AppSpecFull): string {
  return appSpec.blocks[0]?.name ?? 'app'
}

function nodeById(
  topology: StudioActionFlowTopologyContract,
): Map<string, StudioActionFlowStateNode> {
  return new Map(topology.nodes.map((node) => [node.id, node]))
}

function sourceBlockForPrimitive(
  primitive: StudioActionFlowPrimitive,
  topology: StudioActionFlowTopologyContract,
  appSpec: AppSpecFull,
): string {
  const nodes = nodeById(topology)
  const local = [...primitive.outputNodeIds, ...primitive.inputNodeIds]
    .map((id) => nodes.get(id))
    .find((node) => node?.scope === 'local' && node.ownerBlockName)
  return local?.ownerBlockName ?? firstBlockName(appSpec)
}

function triggerForPrimitive(
  primitive: StudioActionFlowPrimitive,
): StudioActionFlowBinding['trigger'] {
  if (primitive.kind === 'fetch-datasource-to-global') return 'init'
  if (primitive.kind === 'subscribe-realtime-source') return 'init'
  if (primitive.kind === 'compute-projection') return 'manual'
  return 'event'
}

function eventTypeForPrimitive(primitive: StudioActionFlowPrimitive): string {
  switch (primitive.kind) {
    case 'set-local-state':
    case 'compute-projection':
      return 'change'
    case 'fetch-datasource-to-global':
    case 'subscribe-realtime-source':
      return 'mount'
    case 'promote-local-to-global':
    case 'write-global-to-datasource':
    case 'navigate':
    case 'guard-by-permission':
    case 'trigger-ui-feedback':
      return 'tap'
    case 'hydrate-local-from-global':
      return 'load'
  }
}

function bindingSourceForTrigger(
  trigger: StudioActionFlowBinding['trigger'],
): StudioActionFlowBinding['event']['source'] {
  if (trigger === 'init') return 'lifecycle-init'
  if (trigger === 'manual') return 'manual'
  return 'component-event'
}

export function buildActionFlowBindingPlan(
  appSpec: AppSpecFull,
  options: BuildActionFlowBindingPlanOptions = {},
): StudioActionFlowBindingPlan {
  const topology =
    appSpec.actionFlowTopology ??
    buildActionFlowTopologyContract(appSpec, options)
  const source = options.source ?? 'studio-actionflow-binding-plan'
  const bindings = topology.primitives
    .filter((primitive) => primitive.required)
    .map<StudioActionFlowBinding>((primitive) => {
      const trigger = triggerForPrimitive(primitive)
      const blockName = sourceBlockForPrimitive(primitive, topology, appSpec)
      return {
        id: `binding:${stableId(primitive.id, primitive.kind)}`,
        primitiveRef: primitive.id,
        primitiveKind: primitive.kind,
        flowName: flowNameForPrimitive(primitive),
        trigger,
        event: {
          blockName,
          eventType: eventTypeForPrimitive(primitive),
          source: bindingSourceForTrigger(trigger),
          evidenceRefs: primitive.evidenceRefs,
        },
        inputNodeIds: primitive.inputNodeIds,
        outputNodeIds: primitive.outputNodeIds,
        effectBoundaryRefs: primitive.effectBoundaryRefs,
        expectedResultKey: resultKeyForPrimitive(primitive),
        ...(primitive.compilerTargetAction
          ? { compilerTargetAction: primitive.compilerTargetAction }
          : {}),
        ...(primitive.compilerTargetStepType
          ? { compilerTargetStepType: primitive.compilerTargetStepType }
          : {}),
        evidenceRefs: primitive.evidenceRefs,
      }
    })

  return {
    schemaVersion: 'studio-actionflow-binding-plan.v1',
    source,
    generatedAt: new Date().toISOString(),
    bindings,
  }
}

function compilePrimitiveSteps(
  binding: StudioActionFlowBinding,
  primitive: StudioActionFlowPrimitive,
  topology: StudioActionFlowTopologyContract,
): FlowStep[] {
  const nodes = nodeById(topology)
  const input = nodes.get(binding.inputNodeIds[0])
  const output = nodes.get(binding.outputNodeIds[0])
  const resultKey =
    binding.expectedResultKey ?? resultKeyForPrimitive(primitive)

  switch (primitive.kind) {
    case 'set-local-state':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'setState',
          params: {
            target: setStateTargetForNode(output),
            value: '#{event.value}',
          },
        },
      ]
    case 'promote-local-to-global':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'setState',
          params: {
            target: setStateTargetForNode(output),
            value: expressionForNode(input),
          },
        },
      ]
    case 'hydrate-local-from-global':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'setState',
          params: {
            target: setStateTargetForNode(output),
            value: expressionForNode(input),
          },
        },
      ]
    case 'fetch-datasource-to-global':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'restApi',
          params: {
            datasourceName: input?.datasourceName ?? input?.path,
            method: 'GET',
            resultTarget: `_results.${resultKey}`,
          },
        },
        {
          type: 'basic',
          action: 'setState',
          params: {
            target: setStateTargetForNode(output),
            value: `#{_results.${resultKey}.data}`,
          },
        },
      ]
    case 'write-global-to-datasource':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'restApi',
          params: {
            datasourceName: output?.datasourceName ?? output?.path,
            method: 'POST',
            body: expressionForNode(input),
            resultTarget: `_results.${resultKey}`,
          },
        },
      ]
    case 'compute-projection':
      return [
        {
          type: 'expression',
          action: 'compute',
          params: {
            target: output?.path ?? 'computed.value',
            expression: expressionForNode(input),
          },
        },
      ]
    case 'navigate':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'navigate',
          params: {
            to: expressionForNode(input),
          },
        },
      ]
    case 'guard-by-permission':
      return [
        {
          type: 'conditional',
          condition: expressionForNode(input),
          then: [
            {
              type: 'basic',
              name: resultKey,
              action: 'setState',
              params: {
                target: `_results.${resultKey}`,
                value: { allowed: true },
              },
            },
          ],
          else: [
            {
              type: 'basic',
              action: 'setState',
              params: {
                target: `_results.${resultKey}`,
                value: { allowed: false, reason: 'permission-denied' },
              },
            },
          ],
        },
      ]
    case 'trigger-ui-feedback':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'showToast',
          params: {
            message:
              primitive.compilerParamBindings?.find(
                (binding) => binding.role === 'payload',
              )?.value ?? 'Action completed',
            variant: 'success',
          },
        },
      ]
    case 'subscribe-realtime-source':
      return [
        {
          type: 'basic',
          name: resultKey,
          action: 'restApi',
          params: {
            datasourceName: input?.datasourceName ?? input?.path,
            method: 'SUBSCRIBE',
            resultTarget: `_results.${resultKey}`,
          },
        },
        {
          type: 'basic',
          action: 'setState',
          params: {
            target: setStateTargetForNode(output),
            value: `#{_results.${resultKey}.data}`,
          },
        },
      ]
  }
}

export function compileActionFlowBindingPlan(
  topology: StudioActionFlowTopologyContract,
  bindingPlan: StudioActionFlowBindingPlan,
): FlowMap {
  const primitives = new Map(
    topology.primitives.map((primitive) => [primitive.id, primitive]),
  )
  return Object.fromEntries(
    bindingPlan.bindings.flatMap((binding): Array<[string, Flow]> => {
      const primitive = primitives.get(binding.primitiveRef)
      if (!primitive) return []
      return [
        [
          binding.flowName,
          {
            name: binding.flowName,
            trigger: binding.trigger,
            steps: compilePrimitiveSteps(binding, primitive, topology),
          },
        ],
      ]
    }),
  )
}

export function validateActionFlowCompilerArtifacts(
  appSpec: AppSpecFull,
  topology: StudioActionFlowTopologyContract | null | undefined,
  bindingPlan: StudioActionFlowBindingPlan | null | undefined,
  flows: FlowMap | null | undefined,
  generationAuthority: StudioActionFlowGenerationAuthority = buildActionFlowGenerationAuthority(
    flows,
    { targetPlatform: generationTargetForAppSpec(appSpec) },
  ),
): StudioActionFlowCompilerProof {
  const issues: StudioActionFlowCompilerIssue[] = []
  const addIssue = (issue: StudioActionFlowCompilerIssue) => issues.push(issue)

  if (!topology) {
    return {
      schemaVersion: 'studio-actionflow-compiler-proof.v1',
      valid: false,
      issues: [
        {
          code: 'missing-topology',
          severity: 'error',
          message: 'ActionFlow compiler requires a state topology contract.',
        },
      ],
      summary: {
        requiredPrimitives: 0,
        boundPrimitives: 0,
        compiledFlows: 0,
        missingPrimitiveRefs: [],
      },
    }
  }

  const requiredPrimitives = topology.primitives.filter(
    (primitive) => primitive.required,
  )
  if (!bindingPlan) {
    return {
      schemaVersion: 'studio-actionflow-compiler-proof.v1',
      valid: false,
      issues: [
        {
          code: 'missing-binding-plan',
          severity: 'error',
          message: 'ActionFlow compiler requires a binding plan.',
        },
      ],
      summary: {
        requiredPrimitives: requiredPrimitives.length,
        boundPrimitives: 0,
        compiledFlows: 0,
        missingPrimitiveRefs: requiredPrimitives.map(
          (primitive) => primitive.id,
        ),
      },
    }
  }

  const blocks = new Set(appSpec.blocks.map((block) => block.name))
  const primitives = new Map(
    topology.primitives.map((primitive) => [primitive.id, primitive]),
  )
  const nodes = nodeById(topology)
  const boundaries = new Map(
    topology.effectBoundaries.map((boundary) => [boundary.id, boundary]),
  )
  const bindingsByPrimitive = new Map(
    bindingPlan.bindings.map((binding) => [binding.primitiveRef, binding]),
  )
  const missingPrimitiveRefs = requiredPrimitives
    .filter((primitive) => !bindingsByPrimitive.has(primitive.id))
    .map((primitive) => primitive.id)

  for (const authorityIssue of generationAuthority.issues) {
    addIssue({
      code:
        authorityIssue.reason === 'capability-missing'
          ? 'compiled-action-capability-missing'
          : 'compiled-action-generation-forbidden',
      severity: 'error',
      flowName: authorityIssue.flowNames.join(', '),
      message: `Action "${authorityIssue.actionName}" is not admitted by the canonical generation authority (${authorityIssue.reason}).`,
    })
  }

  for (const primitiveRef of missingPrimitiveRefs) {
    addIssue({
      code: 'missing-primitive-binding',
      severity: 'error',
      primitiveId: primitiveRef,
      message: `Required primitive "${primitiveRef}" has no ActionFlow binding.`,
    })
  }

  for (const binding of bindingPlan.bindings) {
    const primitive = primitives.get(binding.primitiveRef)
    if (!primitive) {
      addIssue({
        code: 'binding-missing-primitive',
        severity: 'error',
        bindingId: binding.id,
        primitiveId: binding.primitiveRef,
        message: `Binding "${binding.id}" references missing primitive "${binding.primitiveRef}".`,
      })
      continue
    }
    if (!blocks.has(binding.event.blockName)) {
      addIssue({
        code: 'binding-missing-source-block',
        severity: 'error',
        bindingId: binding.id,
        message: `Binding "${binding.id}" references missing source block "${binding.event.blockName}".`,
      })
    }
    for (const nodeId of binding.outputNodeIds) {
      const node = nodes.get(nodeId)
      if (
        node?.scope === 'local' &&
        node.ownerBlockName &&
        node.ownerBlockName !== binding.event.blockName
      ) {
        addIssue({
          code: 'binding-cross-block-local-output',
          severity: 'error',
          bindingId: binding.id,
          message: `Binding "${binding.id}" writes local state owned by "${node.ownerBlockName}" from "${binding.event.blockName}".`,
        })
      }
    }
    if (
      (primitive.kind === 'fetch-datasource-to-global' ||
        primitive.kind === 'write-global-to-datasource' ||
        primitive.kind === 'subscribe-realtime-source') &&
      !binding.effectBoundaryRefs.some((ref) => boundaries.get(ref)?.sideEffect)
    ) {
      addIssue({
        code: 'datasource-binding-missing-effect-boundary',
        severity: 'error',
        bindingId: binding.id,
        primitiveId: primitive.id,
        message: `Datasource binding "${binding.id}" must preserve a side-effect boundary.`,
      })
    }

    const flow = flows?.[binding.flowName]
    if (!flow) {
      addIssue({
        code: 'compiled-flow-missing',
        severity: 'error',
        bindingId: binding.id,
        flowName: binding.flowName,
        message: `Binding "${binding.id}" has no compiled flow "${binding.flowName}".`,
      })
      continue
    }
    if (!FLOW_TRIGGERS.has(flow.trigger)) {
      addIssue({
        code: 'compiled-flow-invalid-trigger',
        severity: 'error',
        bindingId: binding.id,
        flowName: binding.flowName,
        message: `Compiled flow "${binding.flowName}" has invalid trigger "${flow.trigger}".`,
      })
    }
    if (!Array.isArray(flow.steps) || flow.steps.length === 0) {
      addIssue({
        code: 'compiled-flow-empty',
        severity: 'error',
        bindingId: binding.id,
        flowName: binding.flowName,
        message: `Compiled flow "${binding.flowName}" has no steps.`,
      })
    }
  }

  return {
    schemaVersion: 'studio-actionflow-compiler-proof.v1',
    valid: !issues.some((issue) => issue.severity === 'error'),
    issues,
    summary: {
      requiredPrimitives: requiredPrimitives.length,
      boundPrimitives: requiredPrimitives.length - missingPrimitiveRefs.length,
      compiledFlows: Object.keys(flows ?? {}).length,
      missingPrimitiveRefs,
    },
  }
}

export function withActionFlowCompilerArtifacts(
  appSpec: AppSpecFull,
  options: BuildActionFlowBindingPlanOptions = {},
): AppSpecFull {
  const topologySpec = withActionFlowTopologyContract(appSpec, options)
  const topology = topologySpec.actionFlowTopology!
  const bindingPlan =
    topologySpec.actionFlowBindingPlan ??
    buildActionFlowBindingPlan(topologySpec, options)
  const flows =
    topologySpec.actionFlowCompiledFlows ??
    compileActionFlowBindingPlan(topology, bindingPlan)
  const generationAuthority = buildActionFlowGenerationAuthority(flows, {
    targetPlatform: generationTargetForAppSpec(topologySpec),
  })

  return {
    ...topologySpec,
    actionFlowBindingPlan: bindingPlan,
    actionFlowCompiledFlows: flows,
    actionFlowGenerationAuthority: generationAuthority,
    actionFlowCompilerProof: validateActionFlowCompilerArtifacts(
      topologySpec,
      topology,
      bindingPlan,
      flows,
      generationAuthority,
    ),
  }
}
